import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { server } from "../server";
import { enrichCartWithPostageFees } from "../utils/checkoutPricing";

/**
 * Fetches product postage when cart lines omit it (e.g. accepted offers with partial populate).
 * Checkout and payment use the same pattern; cart now shares this hook.
 */
export default function useEnrichedCartPostage(cart) {
  const [postageByProductId, setPostageByProductId] = useState({});

  useEffect(() => {
    if (!Array.isArray(cart) || cart.length === 0) return undefined;

    const missingIds = [
      ...new Set(
        cart
          .filter((item) => {
            const raw = item?.postageFees ?? item?.postageFee ?? item?.shippingFee;
            return raw === undefined || raw === null;
          })
          .map((item) => item?._id || item?.productId || item?.id)
          .filter(Boolean)
          .map(String)
      ),
    ].filter((id) => postageByProductId[id] === undefined);

    if (!missingIds.length) return undefined;

    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        missingIds.map(async (id) => {
          try {
            const { data } = await axios.get(`${server}/product/get-product/${id}`);
            updates[id] = Number(data?.product?.postageFees ?? 0);
          } catch {
            updates[id] = 0;
          }
        })
      );
      if (!cancelled) {
        setPostageByProductId((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cart, postageByProductId]);

  const cartWithPostage = useMemo(
    () => enrichCartWithPostageFees(cart, postageByProductId),
    [cart, postageByProductId]
  );

  return { cartWithPostage, postageByProductId };
}
